import { Component, Inject, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FulfillmentOrderApiService } from '@vendasta/order-fulfillment';
import { OrderSubmittedComponent } from '@vendasta/orders';
import { Order as SalesOrder, SalesOrdersService } from '@vendasta/sales-orders';
import { User as LibUser } from '@vendasta/users';
import {
  SendEmailDialogComponent,
  SendEmailDialogResp,
  WorkOrderFormService,
  WorkOrderFormUnsavedChangesInterface,
  WorkOrderUnsavedChangesComponent,
} from '@vendasta/work-order';
import { combineLatest, from, Observable, of, Subscription } from 'rxjs';
import { map, mergeMap, shareReplay, switchMap, take, toArray } from 'rxjs/operators';
import { AccountService } from '../../core/account.service';
import { PARTNER_ID_INJECTION_TOKEN } from '../../core/constants';
import { User } from '../../core/user';

@Component({
  templateUrl: './order-submitted-page.component.html',
  styleUrls: ['./order-submitted-page.component.scss'],
  standalone: false,
})
export class OrderSubmittedPageComponent implements OnInit, OnDestroy, WorkOrderFormUnsavedChangesInterface {
  users$: Observable<User[]>;
  salesOrder$: Observable<SalesOrder>;
  salesOrderUrl$: Observable<string>;
  accountDetailsUrl$: Observable<string>;
  allUsers: LibUser[];
  partnerId: string;
  businessId: string;
  salesOrderId: string;
  private subscriptions: Subscription[] = [];

  protected showShareFulfillmentFormSignal = signal<boolean>(false);

  @ViewChild('orderSubmittedComponent') orderSubmittedComponent: OrderSubmittedComponent;

  constructor(
    @Inject(PARTNER_ID_INJECTION_TOKEN) private readonly partnerId$: Observable<string>,
    private accountService: AccountService,
    private salesOrderService: SalesOrdersService,
    private route: ActivatedRoute,
    private fulfillmentOrderService: FulfillmentOrderApiService,
    private snackbarService: SnackbarService,
    private workOrderFormService: WorkOrderFormService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.salesOrder$ = this.route.queryParams.pipe(
      take(1),
      switchMap((params: Params) => {
        this.businessId = params.accountGroupId;
        this.salesOrderId = params.orderId;
        return this.salesOrderService.get(params.orderId, params.accountGroupId);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.subscriptions.push(
      combineLatest([this.route.queryParams, this.partnerId$])
        .pipe(
          switchMap(([params, partnerId]) => {
            this.partnerId = partnerId;
            this.businessId = params.accountGroupId;
            return this.accountService.loadUsers(params.accountGroupId, partnerId);
          }),
        )
        .subscribe((users: User[]) => {
          this.allUsers = users.map((user) => this.convertToLibUser(user));
        }),
    );

    this.salesOrderUrl$ = this.salesOrder$.pipe(
      map((salesOrder) => `/order-management/${salesOrder.businessId}/info/${salesOrder.orderId}`),
    );
    this.accountDetailsUrl$ = this.salesOrder$.pipe(map((order) => `/businesses/accounts/${order.businessId}/details`));

    this.users$ = this.accountService.users$;
  }

  getWorkOrderDetailsComponent(): WorkOrderUnsavedChangesComponent {
    return this.orderSubmittedComponent;
  }

  openSendEmailDialog(): void {
    const afterClosed$: Observable<SendEmailDialogResp> = this.dialog
      .open(SendEmailDialogComponent, {
        width: '600px',
        data: {
          allUsers: this.allUsers,
          partnerId: this.partnerId,
          businessId: this.businessId,
          allowUserManagement: true,
        },
      })
      .afterClosed();

    combineLatest([
      this.workOrderFormService.getOrdersToRenderFormsFor(this.salesOrderId, this.businessId, false),
      afterClosed$,
    ])
      .pipe(
        take(1),
        switchMap(([workOrders, resp]) => {
          if (!resp?.userIds || resp?.userIds?.length === 0) {
            return of(undefined);
          }
          const itemNames = workOrders.map((wo) => wo.appInfo.appName);
          return from(resp.userIds).pipe(
            mergeMap((userId) =>
              this.fulfillmentOrderService.sendFulfillmentOrder({
                businessId: this.businessId,
                salesOrderId: this.salesOrderId,
                userId: userId,
                itemNames: itemNames,
              }),
            ),
            toArray(),
          );
        }),
      )
      .subscribe({
        next: (resp) => {
          if (resp) {
            this.snackbarService.openSuccessSnack('Successfully sent email');
          }
        },
        error: () => this.snackbarService.openErrorSnack('Failed to send email'),
      });
  }

  public convertToLibUser(user: User): LibUser {
    return {
      userId: user.userId,
      unifiedId: user.unifiedUserId,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
    } as LibUser;
  }

  protected showShareFulfillmentForm(show: boolean): void {
    this.showShareFulfillmentFormSignal.set(show);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
